import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import api from "../../utils/api";

const initialFilters = Object.freeze({
  userId: "",
  firstName: "",
  lastName: "",
  date: "",
  startDate: "",
  endDate: "",
});

const methodColors = {
  GET: "info",
  POST: "success",
  PUT: "warning",
  PATCH: "warning",
  DELETE: "error",
};

const buildParams = (filters) => {
  const params = {};
  if (filters.userId) params.user_id = filters.userId.trim();
  if (filters.firstName) params.first_name = filters.firstName.trim();
  if (filters.lastName) params.last_name = filters.lastName.trim();
  if (filters.date) params.date = filters.date;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  return params;
};

const formatUserLabel = (log) => {
  if (!log.user_id) return "Anonymous";
  const first = log.user_first_name || "";
  const last = log.user_last_name || "";
  const name = `${first} ${last}`.trim();
  if (name) {
    return `${name} (#${log.user_id})`;
  }
  return `${log.user_username || "User"} (#${log.user_id})`;
};

const capitalizeLabel = (value, fallback) => {
  const text = (value || "").toString().trim();
  if (!text) return fallback;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatDeviceInfo = (log) => {
  const device = capitalizeLabel(log.device_type, "Unknown device");
  const browser = capitalizeLabel(log.browser, "Unknown browser");
  const os = capitalizeLabel(log.operating_system, "Unknown OS");
  return `${device} (${browser}, ${os})`;
};

export default function UserActivityPage() {
  const [filters, setFilters] = useState({ ...initialFilters });
  const filtersRef = useRef({ ...initialFilters });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // PAGINATION STATE
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const fetchLogs = useCallback(
    async (overrideFilters = null) => {
      const activeFilters = overrideFilters ? { ...overrideFilters } : filtersRef.current;
      setLoading(true);
      setError("");
      try {
        const response = await api.get("/user-activity/", {
          params: buildParams(activeFilters),
        });
        const payload = response.data?.results ?? response.data ?? [];
        const list = Array.isArray(payload) ? payload : [];
        setLogs(list);
        setLastUpdated(new Date().toISOString());
      } catch (err) {
        console.error("Failed to load user activity", err);
        const message =
          err.response?.data?.detail ||
          err.response?.data?.error ||
          err.message ||
          "Failed to load user activity.";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchLogs(initialFilters);
  }, [fetchLogs]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Reset to page 1 whenever logs update
  useEffect(() => {
    setPage(1);
  }, [logs]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchLogs(filters);
  };

  const handleResetFilters = () => {
    const reset = { ...initialFilters };
    filtersRef.current = reset;
    setFilters(reset);
    fetchLogs(reset);
  };

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdated) return "";
    return new Date(lastUpdated).toLocaleString();
  }, [lastUpdated]);

  // Remove anonymous logs
  const filteredLogs = logs.filter(
    (log) => log.user_id !== null && log.user_id !== undefined
  );

  // PAGINATION CALCULATIONS
  const totalPages = Math.ceil(filteredLogs.length / pageSize);

  const paginatedLogs = filteredLogs.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  return (
    <Container sx={{ mt: 5 }}>
      <Typography variant="h4" gutterBottom>
        User Activity
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Review every API interaction captured by the platform. Use the filters
        below to pinpoint actions by user or time frame.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Filters</Typography>
          <Button
            size="small"
            startIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setFiltersOpen((prev) => !prev)}
          >
            {filtersOpen ? "Hide Filters" : "Show Filters"}
          </Button>
        </Box>
        <Collapse in={filtersOpen}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            useFlexGap
            flexWrap="wrap"
            mt={filtersOpen ? 2 : 0}
          >
            <TextField
              label="User ID"
              name="userId"
              value={filters.userId}
              onChange={handleChange}
              type="number"
              fullWidth
              size="small"
            />
            <TextField
              label="First Name"
              name="firstName"
              value={filters.firstName}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={filters.lastName}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Specific Date"
              name="date"
              type="date"
              value={filters.date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Start Date"
              name="startDate"
              type="date"
              value={filters.startDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="End Date"
              name="endDate"
              type="date"
              value={filters.endDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Stack>

          <Box
            mt={3}
            display="flex"
            flexWrap="wrap"
            gap={2}
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
          >
            <Button
              variant="contained"
              startIcon={<FilterAltIcon />}
              onClick={handleApplyFilters}
            >
              Apply Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchLogs(filters)}
            >
              Refresh
            </Button>
            <Button
              color="secondary"
              startIcon={<ClearAllIcon />}
              onClick={handleResetFilters}
            >
              Reset
            </Button>
          </Box>
        </Collapse>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {lastUpdatedLabel && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1}
          >
            Last updated: {lastUpdatedLabel}
          </Typography>
        )}

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={6}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : filteredLogs.length === 0 ? (
          <Typography>No activity logs found for the selected filters.</Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>{formatUserLabel(log)}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.method}
                          color={methodColors[log.method] || "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography variant="body2" noWrap>
                          {formatDeviceInfo(log)}
                        </Typography>
                      </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2">
                        {log.description || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>

            {/* PAGINATION CONTROLS */}
            <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap={2}>
              <Button
                variant="outlined"
                disabled={page === 1}
                onClick={() => setPage((prev) => prev - 1)}
              >
                Previous
              </Button>

              <Typography variant="body2">
                Page {page} of {totalPages}
              </Typography>

              <Button
                variant="outlined"
                disabled={page === totalPages || totalPages === 0}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
}
